<template>
  <a-modal
      v-model:visible="visible"
      :title="isEdit ? '编辑字典类型' : '新增字典类型'"
      @cancel="handleCancel"
      :on-before-ok="handleSubmit"
      width="600px"
      draggable
  >
    <div class="edit-container">
      <a-form
          ref="formRef"
          :model="formData"
          :rules="formRules"
          layout="vertical"
      >
        <a-form-item label="类型编码" field="typeCode">
          <a-input 
              v-model="formData.typeCode" 
              placeholder="请输入类型编码"
              :disabled="isEdit"
              allow-clear
          />
<!--          <div class="form-help-text" v-if="isEdit">编码不可修改</div>-->
        </a-form-item>
        
        <a-form-item label="类型名称" field="typeName">
          <a-input 
              v-model="formData.typeName" 
              placeholder="请输入类型名称"
              allow-clear
          />
        </a-form-item>
        
        <a-form-item label="状态" field="status">
          <a-radio-group v-model="formData.status">
            <a-radio :value="true">启用</a-radio>
            <a-radio :value="false">停用</a-radio>
          </a-radio-group>
        </a-form-item>
        
        <a-form-item label="备注" field="remark">
          <a-textarea 
              v-model="formData.remark" 
              placeholder="请输入备注"
              :auto-size="{ minRows: 3, maxRows: 6 }"
              allow-clear
          />
        </a-form-item>
      </a-form>
    </div>
  </a-modal>
</template>

<script setup>
import { ref, watch, nextTick } from 'vue'
import { Message } from '@arco-design/web-vue'
import { getSysDictTypeInfo, createSysDictType, updateSysDictType } from '@/apis/system-data'

// 定义 props
const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  dictTypeId: {
    type: [String, Number],
    default: null
  }
})

// 定义 emits
const emit = defineEmits(['update:modelValue', 'success'])

// 响应式数据
const visible = ref(false)
const loading = ref(false)
const formRef = ref(null)
const isEdit = ref(false)

// 表单数据
const formData = ref({
  typeCode: '',
  typeName: '',
  status: true,
  remark: ''
})

// 表单验证规则
const formRules = ref({
  typeCode: [
    { required: true, message: '请输入类型编码' },
    { minLength: 2, maxLength: 50, message: '类型编码长度为2-50个字符' }
  ],
  typeName: [
    { required: true, message: '请输入类型名称' },
    { minLength: 2, maxLength: 50, message: '类型名称长度为2-50个字符' }
  ],
  status: [
    { required: true, message: '请选择状态' }
  ],
  remark: [
    { maxLength: 500, message: '备注最多500个字符' }
  ]
})

// 监听 modelValue 变化
watch(() => props.modelValue, (newVal) => {
  visible.value = newVal
  if (newVal) {
    resetForm()
    if (props.dictTypeId) {
      isEdit.value = true
      fetchDictTypeInfo()
    } else {
      isEdit.value = false
    }
  }
})

// 监听 visible 变化
watch(visible, (newVal) => {
  emit('update:modelValue', newVal)
})

// 重置表单
const resetForm = () => {
  formData.value = {
    id: '',
    typeCode: '',
    typeName: '',
    status: true,
    remark: ''
  }
  nextTick(() => {
    if (formRef.value) {
      formRef.value?.clearValidate()
    }
  })
}

// 获取字典类型详情
const fetchDictTypeInfo = async () => {
  if (!props.dictTypeId) return
  
  loading.value = true
  try {
    const response = await getSysDictTypeInfo(props.dictTypeId)
    if (response.code === 0) {
      formData.value = response.data
    } else {
      Message.error(response.message || '获取字典类型详情失败')
    }
  } catch (error) {
    Message.error(error.message || '获取字典类型详情失败')
    console.error('获取字典类型详情失败:', error)
  } finally {
    loading.value = false
  }
}

// 提交表单
const handleSubmit = async (done) => {
  console.log(`xxx`)
  try {
    const validateResult = await formRef.value?.validate()
    
    // 如果验证失败，阻止提交和关闭弹框
    if (validateResult !== undefined) {
      done(false)
      return false
    }
    
    loading.value = true
    let response
    
    if (isEdit.value) {
      // 编辑模式
      response = await updateSysDictType(props.dictTypeId, formData.value)
    } else {
      // 新增模式
      response = await createSysDictType(formData.value)
    }
    
    if (response.code === 0) {
      Message.success(isEdit.value ? '更新成功' : '创建成功')
      emit('success')
      visible.value = false
      return true
    } else {
      Message.error(response.message || '操作失败')
      done(false)
      return false
    }
  } catch (error) {
    Message.error(error.message || '操作失败')
    console.error('提交表单失败:', error)
    done(false)
    return false
  } finally {
    done(true)
    loading.value = false
  }
}

// 取消操作
const handleCancel = () => {
  visible.value = false
}
</script>

<style scoped>
.edit-container {
  padding: 0;
}

.form-help-text {
  color: #999;
  font-size: 12px;
  margin-top: 4px;
}
</style>